import * as THREE from 'three'
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js'
import {
  closeAllDoors,
  closeAllDrawers,
  restoreSceneVisibility,
} from './screenshotUtils'

const EXCLUDE_FLAGS = [
  'isBackground',
  'isShadowMan',
  'isGroundShadow',
  'isChair',
  'isLamp',
  'isWazeWithFlowers',
  'isMonitor',
  'isRoom',
] as const

interface HiddenObject {
  object: THREE.Object3D
  originalVisible: boolean
}

function hideNonFurnitureObjects(scene: THREE.Scene): HiddenObject[] {
  const hidden: HiddenObject[] = []

  scene.traverse((object) => {
    const isDecoration = EXCLUDE_FLAGS.some((flag) => object.userData[flag])

    const isLight = object instanceof THREE.Light

    const isClickPlane =
      object instanceof THREE.Mesh &&
      object.material instanceof THREE.MeshBasicMaterial &&
      object.material.opacity === 0 &&
      object.material.transparent

    if ((isDecoration || isLight || isClickPlane) && object.visible) {
      hidden.push({ object, originalVisible: true })
      object.visible = false
    }
  })

  return hidden
}

interface AnimatedObjectState {
  object: THREE.Object3D
  matrixAutoUpdate: boolean
  rotationY: number
  positionZ: number
}

function freezeDoorsAndDrawersClosed(scene: THREE.Scene): AnimatedObjectState[] {
  const saved: AnimatedObjectState[] = []

  scene.traverse((object) => {
    if (!object.userData.isHingeGroup && !object.userData.isDrawerGroup) return

    saved.push({
      object,
      matrixAutoUpdate: object.matrixAutoUpdate,
      rotationY: object.rotation.y,
      positionZ: object.position.z,
    })

    object.matrixAutoUpdate = false
  })

  closeAllDoors(scene)
  closeAllDrawers(scene)

  saved.forEach(({ object }) => {
    object.updateMatrix()
    object.updateMatrixWorld(true)
  })

  return saved
}

function restoreAnimatedObjects(saved: AnimatedObjectState[]): void {
  saved.forEach(({ object, matrixAutoUpdate, rotationY, positionZ }) => {
    object.matrixAutoUpdate = matrixAutoUpdate
    object.rotation.y = rotationY
    object.position.z = positionZ
    object.updateMatrixWorld(true)
  })
}

const CM_TO_M = 0.01

export async function exportSceneAsGLB(
  scene: THREE.Scene
): Promise<ArrayBuffer> {
  const hiddenObjects = hideNonFurnitureObjects(scene)
  const animatedState = freezeDoorsAndDrawersClosed(scene)

  const wrapper = new THREE.Group()
  wrapper.scale.setScalar(CM_TO_M)

  const originalChildren = [...scene.children]
  const visibleChildren = originalChildren.filter((child) => child.visible)

  visibleChildren.forEach((child) => {
    scene.remove(child)
    wrapper.add(child)
  })
  scene.add(wrapper)
  wrapper.updateMatrixWorld(true)

  try {
    const exporter = new GLTFExporter()
    const result = await exporter.parseAsync(wrapper, {
      binary: true,
      onlyVisible: true,
      maxTextureSize: 2048,
    })

    return result as ArrayBuffer
  } finally {
    visibleChildren.forEach((child) => {
      wrapper.remove(child)
      scene.add(child)
    })
    scene.remove(wrapper)

    restoreAnimatedObjects(animatedState)
    restoreSceneVisibility(hiddenObjects)
  }
}

export function triggerDownload(data: ArrayBuffer, filename: string): void {
  const blob = new Blob([data], { type: 'model/gltf-binary' })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()

  requestAnimationFrame(() => {
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  })
}
